import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { api } from '@/api/client';
import { toCommaListIfNumbers, commaListCount } from '@/utils/search-list';

/**
 * The top-bar order search. ONE component for both portals.
 *
 * Shared rather than copied because the merchant version is the same search with a different
 * audience, and a second implementation would drift — which for this component means drifting on
 * WHAT IT RETURNS, not just how it looks.
 *
 * **Tenancy is enforced on the server, not here.** It searches `GET /orders`, where the controller
 * overwrites `merchantId` with the id on the token for any merchant caller and now refuses a
 * `merchantIds` list from that side, and the service intersects the two so a scoped caller can only
 * narrow within their own id. Clicking a result opens the order detail route, which independently
 * refuses an order belonging to anyone else. So a merchant searching an AWB that is not theirs gets
 * "No matching orders" — the same answer as an AWB that does not exist, which is the right answer:
 * confirming a stranger's waybill number exists is itself a leak.
 *
 * Nothing about that depends on this file. Hiding results in the browser would not be a control.
 */
export function GlobalSearch({ placeholder }: { placeholder?: string } = {}) {

  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // A list of numbers ("123,456,789") is looked up number by number on the server, so the dropdown
  // gets room for all of them rather than the usual 8.
  const listCount = debounced.includes(',') ? commaListCount(debounced) : 1;
  const { data, isFetching } = useQuery({
    queryKey: ['global-search', debounced],
    queryFn: async () => (await api.get('/orders', { params: { search: debounced, pageSize: listCount > 1 ? Math.min(100, listCount * 2) : 8 } })).data,
    enabled: debounced.length >= 3,
    staleTime: 30000,
  });
  const results: any[] = (data as any)?.items ?? [];

  useEffect(() => {
    const h = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);

  const goto = (o: any) => {
    setOpen(false); setQ(''); setDebounced('');
    nav(`/orders-${String(o.orderType).toLowerCase()}/${o.id}`);
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && results.length) goto(results[0]);
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    // `min-w-0` so the bar can take space back from the search when the right-hand side is busy —
    // without it an <input> refuses to go below its intrinsic width and the overflow lands on top of
    // whatever comes next. `min-w-[140px]` keeps it usable rather than letting it collapse to a slit.
    <div ref={boxRef} className="relative flex items-center gap-2 flex-1 min-w-[140px] max-w-xl">
      <Search size={18} className="text-slate-400 shrink-0" />
      <input
        type="text"
        value={q}
        // Spaces between NUMBERS become commas (a pasted list of AWBs); a name keeps its spaces.
        onChange={(e) => { setQ(toCommaListIfNumbers(e.target.value)); setOpen(true); }}
        onFocus={() => q.trim().length >= 3 && setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder ?? 'Search AWB, order no, customer mobile / name...'}
        className="flex-1 min-w-0 bg-transparent outline-none text-sm"
      />
      {open && debounced.length >= 3 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {listCount > 1 && results.length > 0 && (
            <div className="px-4 py-1.5 text-[11px] text-slate-500 bg-slate-50 border-b border-slate-100">
              {results.length} order{results.length === 1 ? '' : 's'} found for {listCount} numbers
            </div>
          )}
          {results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-400">{isFetching ? 'Searching…' : 'No matching orders'}</div>
          ) : results.map((o) => (
            <button key={o.id} type="button" onClick={() => goto(o)}
              className="w-full text-left px-4 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm text-slate-800 font-mono">{o.awbNo || o.lrNo || o.orderNo}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{o.orderType}</span>
              </div>
              <div className="text-xs text-slate-500 truncate">{o.orderNo} · {o.customerName} · {o.customerMobile}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
